import {
  Activity,
  Check,
  Info,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";
import React, { useState } from "react";
import { fetchWrapper } from "src/helpers";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import { alertService } from "src/services";

interface RiskAppetiteModalProps {
  onSave: () => void;
}

export const RISK_PROFILE_JUST_SELECTED_KEY = "riskProfileJustSelected";

export const getRiskProfileUserKey = (
  user: {
    uid?: string;
    user_id?: string | number;
    email?: string;
  } | null,
) => {
  if (!user) return "";
  return String(user.uid || user.user_id || user.email || "");
};

export const riskOptions = [
  {
    value: "Low",
    label: "Low Risk",
    description:
      "Capital preservation. Stable returns with minimal volatility.",
    returnText: "Expected return: 4–6%",
    colorClass: "text-[#00A85F]",
    bgClass: "bg-[#00A85F]",
    borderClass: "border-[#00A85F]",
    bars: 1,
    Icon: ShieldCheck,
  },
  {
    value: "Moderate",
    label: "Moderate Risk",
    description: "Balanced growth with controlled exposure to market swings.",
    returnText: "Expected return: 6–9%",
    colorClass: "text-blue-500",
    bgClass: "bg-blue-500",
    borderClass: "border-blue-500",
    bars: 2,
    Icon: Shield,
  },
  {
    value: "High",
    label: "High Risk",
    description:
      "Aggressive growth. Willing to accept notable short-term losses.",
    returnText: "Expected return: 9–13%",
    colorClass: "text-[#F57C00]",
    bgClass: "bg-[#F57C00]",
    borderClass: "border-[#F57C00]",
    bars: 3,
    Icon: ShieldAlert,
  },
  {
    value: "Very High",
    label: "Very High Risk",
    description:
      "Maximum growth potential. Significant volatility and drawdowns.",
    returnText: "Expected return: 13%+",
    colorClass: "text-[#E53935]",
    bgClass: "bg-[#E53935]",
    borderClass: "border-[#E53935]",
    bars: 4,
    Icon: ShieldOff,
  },
];

const SignalBars = ({
  bars,
  colorClass,
}: {
  bars: number;
  colorClass: string;
}) => {
  return (
    <div className="flex items-end gap-[3px] h-[22px]">
      {[1, 2, 3, 4].map((bar) => (
        <div
          key={bar}
          className={`w-1.5 rounded-sm ${
            bar <= bars ? colorClass : "bg-gray-200 dark:bg-gray-700"
          }`}
          style={{ height: `${40 + bar * 15}%` }}
        />
      ))}
    </div>
  );
};

const RiskAppetiteModal: React.FC<RiskAppetiteModalProps> = ({ onSave }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const toggleOption = (val: string) => {
    setSelected((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      alertService.error("Please select at least one risk profile.");
      return;
    }

    setLoading(true);
    try {
      const user = getLocalStorageUser();
      const token = user.token;

      await fetchWrapper.put(
        "/api/profile/update",
        { profileData: { risk_profile: selected } },
        { Authorization: `Bearer ${token}` },
      );

      const updatedUser = { ...user, risk_profile: selected };
      localStorage.setItem("BetaUser", JSON.stringify(updatedUser));

      const userKey = getRiskProfileUserKey(updatedUser);
      if (userKey) {
        sessionStorage.setItem(RISK_PROFILE_JUST_SELECTED_KEY, userKey);
      }

      onSave();
    } catch (_error) {
      alertService.error("Failed to update risk profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  let maxRiskIndex = -1;
  selected.forEach((val) => {
    const idx = riskOptions.findIndex((o) => o.value === val);
    if (idx > maxRiskIndex) maxRiskIndex = idx;
  });

  const meterOption = maxRiskIndex >= 0 ? riskOptions[maxRiskIndex] : null;
  const meterFillPercentage = meterOption ? ((maxRiskIndex + 1) / 4) * 100 : 0;
  const meterColorClass = meterOption ? meterOption.bgClass : "bg-gray-200";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-[520px] flex flex-col overflow-hidden max-h-[95vh]">
        {/* Header */}
        <div className="p-6 pb-2 relative flex items-center gap-4">
          <div className="w-12 h-12 bg-[#111827] rounded-xl flex items-center justify-center shrink-0">
            <Activity className="text-white w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Select your risk profile
            </h2>
            <p className="text-[15px] text-gray-500 dark:text-gray-400 mt-0.5">
              Choose the level that matches your investment comfort.
            </p>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-2 custom-scrollbar">
          {/* Risk Meter */}
          <div className="mb-6 mt-4">
            <div className="flex justify-between items-end mb-2">
              <span className="text-[13px] font-bold text-gray-400 tracking-wider uppercase">
                Risk Meter
              </span>
              <span
                className={`text-[15px] font-semibold ${meterOption ? meterOption.colorClass : "text-transparent"}`}
              >
                {meterOption ? meterOption.label : "None"}
              </span>
            </div>
            <div className="h-3 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`h-full ${meterColorClass} transition-all duration-500 ease-out rounded-full`}
                style={{ width: `${meterFillPercentage}%` }}
              />
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-[13px] font-medium text-gray-400">
                Conservative
              </span>
              <span className="text-[13px] font-medium text-gray-400">
                Aggressive
              </span>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3.5">
            {riskOptions.map((option) => {
              const isSelected = selected.includes(option.value);
              const Icon = option.Icon;

              return (
                <div
                  key={option.value}
                  onClick={() => toggleOption(option.value)}
                  className={`flex items-start gap-4 p-4 rounded-xl border-[1.5px] cursor-pointer transition-all duration-200 ${
                    isSelected
                      ? `${option.borderClass} bg-white dark:bg-gray-800 shadow-[0_2px_10px_rgba(0,0,0,0.04)]`
                      : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600 bg-transparent"
                  }`}
                >
                  <div
                    className={`w-[46px] h-[46px] rounded-[14px] flex items-center justify-center shrink-0 text-white ${option.bgClass} mt-0.5`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </div>

                  <div className="flex-1 min-w-0 pr-2">
                    <h3 className="text-[16px] font-semibold text-gray-900 dark:text-white leading-tight">
                      {option.label}
                    </h3>
                    <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-snug mt-1">
                      {option.description}
                    </p>
                    <p
                      className={`text-[13.5px] font-medium mt-1.5 ${option.colorClass}`}
                    >
                      {option.returnText}
                    </p>
                  </div>

                  <div className="shrink-0 pl-1 pt-1">
                    <SignalBars
                      bars={option.bars}
                      colorClass={option.bgClass}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info Box */}
          <div className="mt-5 mb-2 flex gap-3 p-3.5 bg-[#F9FAFB] dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
            <Info
              className="w-5 h-5 text-gray-400 shrink-0 mt-0.5"
              strokeWidth={1.5}
            />
            <p className="text-[13.5px] text-gray-500 dark:text-gray-400 leading-relaxed">
              You can change your risk profile at any time from Advanced
              Filters. This selection shapes your recommended portfolio
              allocation.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 px-6 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 items-center bg-white dark:bg-gray-900 shrink-0">
          <button
            onClick={handleSave}
            disabled={loading || selected.length === 0}
            className={`flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-[15px] font-medium transition-all ${
              selected.length > 0
                ? "cursor-pointer bg-[#00A85F] hover:bg-[#008f51] dark:bg-gradient-to-t dark:from-[#066766] dark:to-[#0FA243] shadow-md shadow-green-500/20 text-white"
                : "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600"
            }`}
          >
            {loading ? "Saving..." : "Confirm selection"}
            {!loading && <Check className="w-4 h-4" strokeWidth={2.5} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RiskAppetiteModal;
